import { get, set } from "lodash";

import { HEADER_AUTHORIZATION_TOKEN } from "cortex/ui/src/service/base";
import { auth } from "cortex/ui/src/store/auth";

import {
  type ManagerOptions,
  type Socket,
  type SocketOptions,
  io,
} from "socket.io-client";

export const MGR_HEADER_PATH =
  "io.opts.transportOptions.polling.extraHeaders" as const;

interface AuthStore {
  getToken(): string | null;
}

type ExtraHeaders = Record<string, string>;

type SocketInitOptions = Partial<ManagerOptions & SocketOptions> & {
  transportOptions?: {
    polling?: {
      extraHeaders?: ExtraHeaders;
    };
  };
};

function getBearerToken(token: string): ExtraHeaders {
  return {
    [HEADER_AUTHORIZATION_TOKEN]: `Bearer ${token}`,
  };
}

function genSocketHeaders(token: string | null | undefined): SocketInitOptions {
  if (!token) {
    return {};
  }

  return {
    transportOptions: {
      polling: {
        extraHeaders: getBearerToken(token),
      },
    },
  };
}

export function getSocketHeaders(socket: Socket): ExtraHeaders {
  return get(socket, MGR_HEADER_PATH, {}) as ExtraHeaders;
}

export function setSocketTokenHeaders(
  socket: Socket,
  token: string | null | undefined,
): void {
  const extraHeaders: ExtraHeaders = { ...getSocketHeaders(socket) };

  if (token) {
    extraHeaders[HEADER_AUTHORIZATION_TOKEN] = `Bearer ${token}`;
  } else {
    delete extraHeaders[HEADER_AUTHORIZATION_TOKEN];
  }

  set(socket, MGR_HEADER_PATH, extraHeaders);
}

/**
 * Instantiate a Socket.IO client connection
 */
function getSocket(_io: typeof io, _auth: AuthStore): Socket {
  const token = _auth.getToken();
  const socketOptions: SocketInitOptions = {
    path: "/socket.io",
    autoConnect: false,
    reconnection: true,
    transports: ["websocket", "polling"],
    ...genSocketHeaders(token),
  };

  if (token) {
    socketOptions.auth = { token };
  }

  const socketInstance: Socket = _io({
    ...socketOptions,
  });

  return socketInstance;
}

/**
 * Handle reconnect errors, and stop the connection attempt when the user
 * needs to authenticate.
 */
function onReconnectError(this: Socket, error: { description?: number }): void {
  if (error.description === 401) {
    this.disconnect();
  }
}

export const socket: Socket = getSocket(io, auth);

socket.on("reconnect_error", onReconnectError);
